import React, { useEffect, useState } from 'react';
import { Building2, ChevronDown } from 'lucide-react';
import { organizationsService, Organization } from 'api/services/organizations.service';
import { useAuth } from 'hooks/useAuth';

interface OrganizationFilterProps {
  value: string;
  onChange: (organizationId: string) => void;
  className?: string;
  requireSelection?: boolean; // When true, hides "All Organizations" option
  allowServiceProviderSelection?: boolean; // When true, allows selecting service providers (default: false)
}

interface OrganizationGroup {
  serviceProvider: Organization;
  businesses: Organization[];
}

const OrganizationFilter: React.FC<OrganizationFilterProps> = ({
  value,
  onChange,
  className = '',
  requireSelection = false,
  allowServiceProviderSelection = false
}) => {
  const { currentOrganization } = useAuth();
  const [organizationGroups, setOrganizationGroups] = useState<OrganizationGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLabel, setSelectedLabel] = useState('All Organizations');

  const currentRole = currentOrganization?.role?.toLowerCase();
  const isMasterAdmin = currentRole === 'master_admin';
  const isSuperAdmin = currentRole === 'super_admin';
  const isServiceAdmin = currentRole === 'service_admin';
  const isServiceUser = currentRole === 'service_user';

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    // Update label when value changes
    if (!value) {
      setSelectedLabel('All Organizations');
    } else {
      // Check if it's a service provider first
      for (const group of organizationGroups) {
        if (group.serviceProvider.id === value) {
          setSelectedLabel(`${group.serviceProvider.name} (Service Provider)`);
          return;
        }
      }
      // Then check if it's a business
      for (const group of organizationGroups) {
        const business = group.businesses.find(b => b.id === value);
        if (business) {
          setSelectedLabel(business.name);
          return;
        }
      }
    }
  }, [value, organizationGroups]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);

      if (isMasterAdmin) {
        // Master admin sees all service providers and their businesses
        const serviceProvidersResponse = await organizationsService.getServiceProviders();

        if (serviceProvidersResponse.success && serviceProvidersResponse.data) {
          const groups: OrganizationGroup[] = [];

          for (const serviceProvider of serviceProvidersResponse.data) {
            const businessesResponse = await organizationsService.getBusinessesByServiceProvider(
              serviceProvider.id
            );

            groups.push({
              serviceProvider,
              businesses: businessesResponse.success && businessesResponse.data
                ? businessesResponse.data
                : []
            });
          }

          setOrganizationGroups(groups);

          // Auto-select current organization if value is empty
          if (!value && currentOrganization?.id) {
            onChange(currentOrganization.id);
          }
        }
      } else if ((isSuperAdmin || isServiceAdmin || isServiceUser) && currentOrganization) {
        // Super admin, Service admin, and Service user see their service provider and its businesses
        const businessesResponse = await organizationsService.getBusinessesByServiceProvider(
          currentOrganization.id
        );

        if (businessesResponse.success && businessesResponse.data) {
          setOrganizationGroups([{
            serviceProvider: {
              id: currentOrganization.id,
              name: currentOrganization.name,
              type: 'SERVICE' as const,
              businessId: currentOrganization.business_id || '',
              active: true,
              createdAt: '',
              updatedAt: ''
            },
            businesses: businessesResponse.data
          }]);

          // Auto-select current organization (service provider) if value is empty
          if (!value && currentOrganization.id) {
            onChange(currentOrganization.id);
          }
        }
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isMasterAdmin && !isSuperAdmin && !isServiceAdmin && !isServiceUser) {
    // Don't show filter for other roles (only operator_admin and operator_user won't see it)
    return null;
  }

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Building2 className="w-4 h-4 lg:w-5 lg:h-5 text-gray-400" />
        <span className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">Loading organizations...</span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 lg:w-5 lg:h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 lg:px-4 py-2 pr-8 lg:pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white dark:bg-gray-700 text-xs lg:text-sm text-gray-900 dark:text-white"
        >
          {/* Feature Flag: REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA
              When requireSelection=true, "All Organizations" option is hidden to prevent
              multi-tenant pagination issues with operational data (CallLogs, Sessions, etc.)
              To re-enable: set requireSelection=false in the parent component */}
          {isMasterAdmin && !requireSelection && (
            <option value="">All Organizations</option>
          )}

          {organizationGroups.map((group) => (
            <optgroup key={group.serviceProvider.id} label={`📦 ${group.serviceProvider.name}`}>
              {/* Service Provider itself as an option (only if enabled) */}
              {allowServiceProviderSelection && (
                <option key={`sp-${group.serviceProvider.id}`} value={group.serviceProvider.id}>
                  🏢 {group.serviceProvider.name} (All Businesses)
                </option>
              )}

              {/* Individual businesses under the service provider */}
              {group.businesses.map((business) => (
                <option key={business.id} value={business.id}>
                  {allowServiceProviderSelection ? '└─ ' : ''}{business.name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <ChevronDown className="absolute right-3 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  );
};

export default OrganizationFilter;
